import { useRef, useState } from 'react';
import {
  DEPARTMENT_LABEL,
  ROLES,
  ROLE_LABEL,
  checkEmail,
  checkPhone,
  type Department,
  type Role,
  type User,
} from '@/domain';
import { useCreateUser, useProfile, useUpdateUser } from '@/application/usecases/useAdmin';
import { useConfirmAction } from '@/features/shell/useConfirmAction';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import { Modal, modalStyles } from '@/ui/primitives/Modal';
import { toast } from '@/ui/primitives/toastStore';
import styles from './UsersScreen.module.css';
import { PasswordInput } from '@/ui/primitives/PasswordInput';

/** The three benches a technician can be posted to. Reception and Management
 *  have exactly one department each, and CAD / CAM is its own. */
const BENCHES: Department[] = ['plaster', 'wax', 'ceramic'];

function defaultDepartment(role: Role): Department {
  if (role === 'cadcam') return 'cadcam';
  if (role === 'management') return 'management';
  if (role === 'tech') return 'plaster';
  return 'reception';
}

/** What the question names a changed field by: the label the form gives it. */
const FIELD_LABEL: Record<string, string> = {
  name: strings.users.fullName,
  username: strings.users.username,
  title: strings.users.jobTitle,
  role: strings.users.role,
  department: strings.users.department,
  isIntakeOperator: strings.users.intakeOperator,
  isSupervisor: strings.users.supervisor,
  phone: strings.profile.phone,
  email: strings.profile.email,
};

/**
 * Create a user. Section 5.2.
 *
 * The user name is derived from the full name until the field is touched, which
 * is how the prototype behaves and is worth keeping: nobody at reception wants
 * to invent a login handle, and the derived one is always valid.
 *
 * Validation is the server's. This form checks only what it can check without
 * a round trip, and renders the field errors the API returns for the rest, so a
 * taken user name is reported on the field rather than as a bare toast.
 *
 * A valid Create or Save is asked about before it is sent, the request of
 * 5 October 2026 (third), section 2. The question lies over the form, so Cancel
 * comes back to it exactly as it was typed, and a field the server refuses is
 * reported on the field once the question has gone.
 */
export function AddUserModal({ onClose }: { onClose: () => void }) {
  return <UserFormModal onClose={onClose} />;
}

/**
 * Edit a user: the same form, filled in, without the password, which has its
 * own Set password. Request of 4 October 2026, section 3: the supervisor role
 * could only be given when the user was created. Only what changed is sent,
 * and the server logs each change from what it was to what it is. The person
 * need not sign out: the server reads their access on every request, and
 * their open app is told to reload it.
 */
export function EditUserModal({ user, onClose }: { user: User; onClose: () => void }) {
  return <UserFormModal user={user} onClose={onClose} />;
}

function UserFormModal({ user, onClose }: { user?: User; onClose: () => void }) {
  const editing = user !== undefined;
  const create = useCreateUser();
  const update = useUpdateUser();
  const ask = useConfirmAction();
  const pending = create.isPending || update.isPending;
  // Editing never derives the user name from the full name: it already has one.
  const usernameTouched = useRef(editing);

  const [name, setName] = useState(user?.name ?? '');
  const [username, setUsername] = useState(user?.username ?? '');
  const [role, setRole] = useState<Role>(user?.role ?? 'tech');
  const [department, setDepartment] = useState<Department>(user?.department ?? 'plaster');
  const [title, setTitle] = useState(user?.title ?? '');
  const [password, setPassword] = useState('');
  const [isIntakeOperator, setIsIntakeOperator] = useState(user?.isIntakeOperator ?? false);
  // A department supervisor. Section 11 of the request of 20 September 2026.
  const [isSupervisor, setIsSupervisor] = useState(user?.isSupervisor ?? false);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  /*
   * How to reach them, on an edit only: the team list does not carry it, so it
   * is read off their profile. Request of 4 October 2026 (second), section 7.
   */
  const existing = useProfile(user?.id, 'today');
  const [phone, setPhone] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const shownPhone = phone ?? existing.data?.user.phone ?? '';
  const shownEmail = email ?? existing.data?.user.email ?? '';

  function onNameChange(value: string) {
    setName(value);
    if (!usernameTouched.current) {
      setUsername(value.trim().toLowerCase().replace(/[^a-z0-9]+/g, ''));
    }
  }

  function onRoleChange(next: Role) {
    setRole(next);
    setDepartment(next === user?.role ? user.department : defaultDepartment(next));
    if (next !== 'cadcam') setIsIntakeOperator(false);
    // Only the two bench roles supervise a department.
    if (next !== 'tech' && next !== 'cadcam') setIsSupervisor(false);
  }

  function onFailure(error: Error) {
    const fields =
      'fieldErrors' in error ? (error as { fieldErrors: Record<string, string[]> }).fieldErrors : {};
    setErrors(fields);
    if (Object.keys(fields).length === 0) toast(failureWords(error));
  }

  function submit() {
    const trimmed = { name: name.trim(), username: username.trim().toLowerCase() };
    const local: Record<string, string[]> = {};
    if (trimmed.name === '') local.name = ['Enter a name'];
    if (trimmed.username === '') local.username = ['Enter a user name'];
    if (!editing && password.length < 8) local.password = ['Use at least eight characters'];
    const badPhone = checkPhone(shownPhone);
    if (badPhone !== null) local.phone = [badPhone];
    const badEmail = checkEmail(shownEmail);
    if (badEmail !== null) local.email = [badEmail];
    setErrors(local);
    if (Object.keys(local).length > 0) return;

    if (editing) {
      const benchRole = role === 'tech' || role === 'cadcam';
      const next = {
        name: trimmed.name,
        username: trimmed.username,
        title: title.trim() === '' ? user.title : title.trim(),
        role,
        department,
        isIntakeOperator: role === 'cadcam' ? isIntakeOperator : false,
        isSupervisor: benchRole ? isSupervisor : false,
      };
      // What changed, and nothing else, so the log says what was done.
      const changed: Record<string, unknown> = Object.fromEntries(
        Object.entries(next).filter(([key, value]) => user[key as keyof User] !== value),
      );
      if (phone !== null && phone.trim() !== (existing.data?.user.phone ?? '')) changed.phone = phone.trim();
      if (email !== null && email.trim() !== (existing.data?.user.email ?? '')) changed.email = email.trim();
      if (Object.keys(changed).length === 0) {
        onClose();
        return;
      }
      ask(
        strings.confirm.editUser(
          user.name,
          Object.keys(changed).map((key) => FIELD_LABEL[key] ?? key),
        ),
        () =>
          update.mutateAsync({ id: user.id, edit: changed }).then((saved) => {
            toast(`${saved.name} ${strings.users.saved}`);
            onClose();
          }, onFailure),
        { title: `${strings.users.editTitle} ${user.name}`, confirmLabel: strings.users.save },
      );
      return;
    }

    const created = {
      name: trimmed.name,
      username: trimmed.username,
      title: title.trim() === '' ? ROLE_LABEL[role] : title.trim(),
      role,
      department,
      password,
      ...(role === 'cadcam' ? { isIntakeOperator } : {}),
      ...(role === 'tech' || role === 'cadcam' ? { isSupervisor } : {}),
    };
    ask(
      strings.confirm.createUser(created.name, ROLE_LABEL[role]),
      () =>
        create.mutateAsync(created).then((made) => {
          toast(`${made.name} ${strings.users.created} "${made.username}".`);
          onClose();
        }, onFailure),
      { title: strings.users.addTitle, confirmLabel: strings.users.create },
    );
  }

  return (
    <Modal
      title={editing ? `${strings.users.editTitle} ${user.name}` : strings.users.addTitle}
      lead={editing ? strings.users.editLead : strings.users.addLead}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={pending}>
            {strings.users.cancel}
          </Button>
          <Button variant="primary" disabled={pending} onClick={submit}>
            {editing
              ? pending
                ? strings.users.saving
                : strings.users.save
              : pending
                ? strings.users.creating
                : strings.users.create}
          </Button>
        </>
      }
    >
      <Field label={strings.users.fullName} error={errors.name} htmlFor="nu-name">
        <input
          id="nu-name"
          className={modalStyles.input}
          value={name}
          placeholder={strings.users.namePlaceholder}
          onChange={(event) => onNameChange(event.target.value)}
        />
      </Field>

      <Field label={strings.users.username} error={errors.username} htmlFor="nu-username">
        <input
          id="nu-username"
          className={`${modalStyles.input} ${styles.mono}`}
          value={username}
          autoComplete="off"
          placeholder={strings.users.usernamePlaceholder}
          onChange={(event) => {
            usernameTouched.current = true;
            setUsername(event.target.value);
          }}
        />
      </Field>

      <Field label={strings.users.role} error={errors.role} htmlFor="nu-role">
        <select
          id="nu-role"
          className={modalStyles.input}
          value={role}
          onChange={(event) => onRoleChange(event.target.value as Role)}
        >
          {ROLES.map((key) => (
            <option key={key} value={key}>
              {ROLE_LABEL[key]}
            </option>
          ))}
        </select>
      </Field>

      {role === 'tech' && (
        <Field label={strings.users.department} error={errors.department} htmlFor="nu-department">
          <select
            id="nu-department"
            className={modalStyles.input}
            value={department}
            onChange={(event) => setDepartment(event.target.value as Department)}
          >
            {BENCHES.map((key) => (
              <option key={key} value={key}>
                {DEPARTMENT_LABEL[key]}
              </option>
            ))}
          </select>
        </Field>
      )}

      {role === 'cadcam' && (
        <div className={modalStyles.field}>
          <label className={styles.check}>
            <input
              type="checkbox"
              checked={isIntakeOperator}
              onChange={(event) => setIsIntakeOperator(event.target.checked)}
            />
            {strings.users.intakeOperator}
          </label>
          <span className={styles.checkHint}>{strings.users.intakeOperatorHint}</span>
        </div>
      )}

      {/*
        Under "Also does intake", as section 11.2 of the request of 20 September
        2026 asks. For the two bench roles: management already passes Final Check
        from the matrix, and reception and accounts work no department to
        supervise.
      */}
      {(role === 'tech' || role === 'cadcam') && (
        <div className={modalStyles.field}>
          <label className={styles.check}>
            <input
              type="checkbox"
              checked={isSupervisor}
              onChange={(event) => setIsSupervisor(event.target.checked)}
            />
            {strings.users.supervisor}
          </label>
          <span className={styles.checkHint}>{strings.users.supervisorHint}</span>
        </div>
      )}

      <Field label={strings.users.jobTitle} error={errors.title} htmlFor="nu-title">
        <input
          id="nu-title"
          className={modalStyles.input}
          value={title}
          placeholder={strings.users.titlePlaceholder}
          onChange={(event) => setTitle(event.target.value)}
        />
      </Field>

      {editing && (
        <>
          <Field label={strings.users.phone} error={errors.phone} htmlFor="nu-phone">
            <input
              id="nu-phone"
              type="tel"
              inputMode="tel"
              autoComplete="off"
              className={modalStyles.input}
              value={shownPhone}
              placeholder={strings.users.phonePlaceholder}
              onChange={(event) => setPhone(event.target.value)}
            />
          </Field>
          <Field label={strings.users.email} error={errors.email} htmlFor="nu-email">
            <input
              id="nu-email"
              type="email"
              inputMode="email"
              autoComplete="off"
              className={modalStyles.input}
              value={shownEmail}
              placeholder={strings.users.emailPlaceholder}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>
        </>
      )}

      {!editing && (
      <Field label={strings.users.password} error={errors.password} htmlFor="nu-password">
        <PasswordInput
          id="nu-password"
          className={modalStyles.input}
          value={password}
          autoComplete="new-password"
          onChange={(event) => setPassword(event.target.value)}
        />
        <span className={styles.checkHint}>{strings.users.passwordHint}</span>
      </Field>
      )}
    </Modal>
  );
}

function Field({
  label,
  error,
  htmlFor,
  children,
}: {
  label: string;
  error?: string[];
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className={modalStyles.field}>
      <label className={modalStyles.label} htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {error !== undefined && error.length > 0 && (
        <p className={modalStyles.error}>{error.join(' ')}</p>
      )}
    </div>
  );
}
